<template>
  <PageLayout>
    <div class="d-flex align-center justify-space-between">
      <h2 class="text-h3 font-weight-bold pb-4">
        Chytrá publika
      </h2>
      <v-btn
        v-if="!isPublished"
        text
        small
        class="navy--text text--darken-1"
        @click="saveAudience(false)"
      >
        <img class="mr-2" :src="require('@/assets/ico-save-blue.svg')" alt="" />
        Uložit jako rozepsanou
      </v-btn>
    </div>
    <p class="text-h4">
      Pro generování chytrých publik vám stačí vyplnit pouze ty nejdůležitější
      položky
    </p>

    <ui-box label="Základní informace" ref="saForm">
      <v-row>
        <v-col cols="12">
          <v-text-field
            key="name"
            outlined
            hide-details="auto"
            placeholder="Název chytrého publika"
            @input="isValid('name')"
            :error-messages="getValidationsErrors('name')"
            v-model="name"
          />
        </v-col>
        <v-col cols="12">
          <v-select
            v-model="pixelId"
            hide-details="auto"
            label="Pixel"
            outlined
            :items="pixels"
            item-text="name"
            item-value="id"
            @input="isValid('pixelId')"
            :error-messages="getValidationsErrors('pixelId')"
          />
        </v-col>
        <v-col cols="12">
          <v-select
            v-model="pageId"
            outlined
            label="Stránka"
            :items="pages"
            item-text="name"
            item-value="id"
            hide-details="auto"
            @input="isValid('pageId')"
            :error-messages="getValidationsErrors('pageId')"
          />
        </v-col>
      </v-row>
    </ui-box>

    <ui-box
      v-for="(item, index) in products"
      :key="index"
      class="mt-5"
      label="Produkt"
    >
      <v-row v-for="(row, i) in items" :key="`${index}-${i}`">
        <v-col cols="12">
          <v-text-field
            :label="row.name"
            outlined
            type="text"
            hide-details
            :disabled="draftId && products[index][row.path] === ''"
            :value="products[index][row.path]"
            @input="
              value => {
                onUpdateValue(index, row.path, value)
              }
            "
          />
        </v-col>
      </v-row>
      <v-btn
        text
        small
        v-if="!draftId && products.length > 1"
        class="primary--text ml-n2 mb-4 mt-4"
        @click="onRemoveProduct(index)"
      >
        <v-icon left small>
          mdi-delete-outline
        </v-icon>
        Odstranit produkt
      </v-btn>
    </ui-box>
    <v-btn
      text
      small
      class="primary--text ml-n2 mb-4 mt-4"
      @click="onAddProduct()"
      v-if="!draftId"
    >
      <v-icon left small>
        mdi-plus-circle-outline
      </v-icon>
      Přidat další produkt
    </v-btn>

    <div class="d-flex align-center justify-space-between mt-5">
      <v-btn
        class="c-btn"
        min-width="186"
        @click="$router.push('/smart-audiences/')"
      >
        Zrušit
      </v-btn>
      <v-btn
        color="primary"
        class="c-btn"
        min-width="186"
        @click="saveAudience(true)"
        :loading="publishing"
      >
        {{ draftId ? "Upravit" : "Vytvořit" }}
      </v-btn>
    </div>
  </PageLayout>
</template>

<script>
import PageLayout from "@/components/page/home/PageLayout.vue"
import { call, get, sync } from "vuex-pathify"
import SmartAudiencesAPI from "@/api/smartAudiences"
import validation from "@/mixins/validation"
import { required } from "vuelidate/lib/validators"

export default {
  name: "SmartAudiencesGeneral",
  components: {
    PageLayout,
  },
  mixins: [validation],
  data() {
    return {
      validationMessages: {
        name: {
          required: "Toto pole je povinné",
        },
        pixelId: {
          required: "Toto pole je povinné",
        },
        pageId: {
          required: "Toto pole je povinné",
        },
      },
      publishing: false,
      items: [
        {
          name: "Název produktu / služby",
          path: "name",
        },
        {
          name: "Blog",
          path: "blog",
        },
        {
          name: "Magnet",
          path: "magnet",
        },
        {
          name: "Děkovací stránka za stažení magnetu",
          path: "magnetThanks",
        },
        {
          name: "Prodejní stránka",
          path: "productSales",
        },
        {
          name: "Objednávková stránka",
          path: "productOrder",
        },
        {
          name: "Děkovací stránka za objednávku / zakoupení produktu",
          path: "productThanks",
        },
      ],
    }
  },
  validations: {
    name: {
      required,
    },
    pixelId: {
      required,
    },
    pageId: {
      required,
    },
  },
  methods: {
    ...call("pixels", ["loadPixels"]),
    ...call("pages", ["loadPages"]),
    ...call("smartAudiences", [
      "updateProductUrl",
      "addProduct",
      "removeProduct",
      "saveSmartAudienceDraft",
      "updateSmartAudienceDraft",
      "getSmartAudienceDraft",
    ]),
    saveAudience(publish = false) {
      if (!this.isValidAll()) {
        return
      }
      this.publishing = true
      const fn = this.draftId
        ? this.updateSmartAudienceDraft({ draftId: this.draftId })
        : this.saveSmartAudienceDraft()

      if (publish) {
        fn.then(response => {
          SmartAudiencesAPI.publishSmartAudienceDraft(response.id).then(() => {
            this.publishing = false
            this.$router.push("/smart-audiences?success")
          })
        })
      } else {
        return fn.then(() => {
          this.publishing = false
          this.$router.push("/smart-audiences?success")
        })
      }
    },
    onUpdateValue(index, path, value) {
      this.updateProductUrl({ index, path, value })
    },
    onAddProduct() {
      this.addProduct()
    },
    onRemoveProduct(index) {
      this.removeProduct({ index })
    },
  },
  computed: {
    ...sync("smartAudiences/model@", ["name", "isPublished"]),
    ...sync("smartAudiences/model@spec", ["pixelId", "pageId"]),
    ...get("pages", ["pages"]),
    ...get("pixels", ["pixels"]),
    ...get("smartAudiences/model@", ["products"]),
    draftId() {
      return this.$route.params.draftId
    },
  },
  mounted() {
    if (this.$route.params.draftId) {
      this.getSmartAudienceDraft({ draftId: this.$route.params.draftId })
    }
    this.loadPixels()
    this.loadPages()
  },
}
</script>
